import {
  BehaviorSubject,
  catchError,
  combineLatest,
  concat,
  EMPTY,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError,
  withLatestFrom,
} from 'rxjs';
import {
  ChangeDetectorRef,
  Injectable,
  OnDestroy,
  ViewContainerRef,
} from '@angular/core';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import moment from 'moment-timezone';

import {PhoneVerificationReq} from 'src/app/shared/components/phone-verification-dialog/PhoneVerificationReq.model';
import {PhoneVerificationReqRes} from 'src/app/shared/components/phone-verification-dialog/PhoneVerificationReqRes.model';
import {UnitBankAccountService} from 'src/app/shared/components/unit/unit-bank-account/unit-bank-account.service';
import {CreateCustomerTokenDialogContext} from 'src/app/shared/components/unit/unit-card/unit-card-create-customer-token/CreateCustomerTokenDialogContext';
import {UnitCardCreateCustomerTokenComponent} from 'src/app/shared/components/unit/unit-card/unit-card-create-customer-token/unit-card-create-customer-token.component';
import {BasePhoneVerificationStrategy} from 'src/app/shared/models/phone-verification-strategies/base-phone-verification-strategy.model';
import {UnitBusinessVirtualDebitCardDto} from 'src/app/shared/models/unit-card/UnitBusinessVirtualDebitCardDto.model';
import {UnitCardDto} from 'src/app/shared/models/unit-card/UnitCardDto.model';
import {UnitCustomerTokenDto} from 'src/app/shared/models/unit-card/UnitCustomerTokenDto.model';
import {StorageService} from '../storage.service';
import {UnitCardDataService} from './unit-card-data.service';
import {UtilsService} from '../utils.service';
import {BaseResponseDto} from 'src/app/shared/models/BaseResponseDto.model';
import {AnalyticsServiceNameModel, TelemetryService} from 'telemetry-library';
import {
  EventsTelemetry,
  EventTelemetryInstance,
} from 'src/app/shared/components/unit/unit-card/TelemetryEvent';
import {AppSettingsService, SubdomainEnum} from '../app-settings.service';
import {UnitPinStatus} from 'src/app/shared/models/unit-card/unit-pin-status.enum';
import UnitCardStatus from 'src/app/shared/enums/unit-card/UnitCardStatus.enum';
import {
  UnitCardPinAction,
  UnitCardPinParams,
} from 'src/app/shared/components/unit/unit-card/unit-card-pin-dialog/UnitCardPinContext';
import {UnitCardPinDialogComponent} from 'src/app/shared/components/unit/unit-card/unit-card-pin-dialog/unit-card-pin-dialog.component';
import {DialogService} from '../dialog.service';
import {SnackbarService} from '../snackbar.service';

@Injectable()
export class UnitCardService
  extends BasePhoneVerificationStrategy
  implements OnDestroy {
  private _destroyObserver$ = new Subject<void>();
  private _unitBankAccountId;
  private _isLpSide = this.appSettingService.subDomain === SubdomainEnum.LP;

  private _unitBankAccountId$ = this.unitBankAccountService.bankAccountId$;
  public unitBankAccount$ = this.unitBankAccountService.bankAccount$;

  private _unitCards$: BehaviorSubject<UnitCardDto[]> = new BehaviorSubject<
    UnitCardDto[]
  >([]);
  public unitCards$ = this._unitCards$.asObservable();

  private _unitBusinessCards$: BehaviorSubject<
    UnitBusinessVirtualDebitCardDto[]
  > = new BehaviorSubject<UnitBusinessVirtualDebitCardDto[]>(null);

  _refreshUnitBusinessCards$ = new BehaviorSubject<void>(null);

  public unitBusinessCards$ = concat(
    this.getBusinessDebitCardByBankAccount(),
    this._unitBusinessCards$
  ).pipe(shareReplay(1));

  private _unitBusinessCardInd$ = new BehaviorSubject<number>(null);
  public unitBusinessCardInd$ = this._unitBusinessCardInd$.pipe(shareReplay(1));
  public unitBusinessCard$ = this._unitBusinessCardInd$.pipe(
    filter((ind) => ind !== null),
    withLatestFrom(this._unitBusinessCards$),
    map(([ind, unitBusinessCards]) => unitBusinessCards[ind]),
    shareReplay(1)
  );

  public unitBusinessCardId$ = this.unitBusinessCard$.pipe(
    map((unitBusinessCard) => unitBusinessCard.id),
    shareReplay(1)
  );

  private _unitBusinessCardDraft$: BehaviorSubject<UnitBusinessVirtualDebitCardDto> =
    new BehaviorSubject<UnitBusinessVirtualDebitCardDto>(null);
  public unitBusinessCardDraft$ = concat(
    this.getBusinessDebitCardDraft(),
    this._unitBusinessCardDraft$
  ).pipe(shareReplay(1));

  private _viewSensitiveData$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  public viewSensitiveData$ = this._viewSensitiveData$.pipe(shareReplay(1));

  private _loadingSensitiveData$ = new BehaviorSubject<boolean>(false);
  public loadingSensitiveData$ = this._loadingSensitiveData$.pipe(
    shareReplay(1)
  );

  private _unitCustomerToken$: BehaviorSubject<UnitCustomerTokenDto> =
    new BehaviorSubject<UnitCustomerTokenDto>(null);
  public unitCustomerToken$ = this._unitCustomerToken$.pipe(
    withLatestFrom(this.unitBankAccount$),
    switchMap(([unitCustomerToken, unitBankAccount]) => {
      if (this.unitCustomerTokenIsValid(unitCustomerToken)) {
        return of(unitCustomerToken);
      }

      const unitCustomerTokenFromStorage =
        this.storageService.getUnitCustomerToken(
          unitBankAccount.unitApplication.unitCustomerId
        );
      if (this.unitCustomerTokenIsValid(unitCustomerTokenFromStorage)) {
        this.updateUnitCustomerToken(
          unitCustomerTokenFromStorage,
          unitBankAccount.unitApplication.unitCustomerId,
          false
        );
        return of(unitCustomerTokenFromStorage);
      }

      return this.sensitiveData(unitBankAccount.unitApplication.unitCustomerId);
    })
  );

  constructor(
    private unitCardDataService: UnitCardDataService,
    private unitBankAccountService: UnitBankAccountService,
    private storageService: StorageService,
    private appSettingService: AppSettingsService,
    private utilService: UtilsService,
    private dialog: MatDialog,
    private vcr: ViewContainerRef,
    private cdr: ChangeDetectorRef,
    private telemetryService: TelemetryService,
    private dialogService: DialogService,
    private snackbarService: SnackbarService
  ) {
    super();
    this._unitBankAccountId$
      .pipe(takeUntil(this._destroyObserver$))
      .subscribe(
        (unitBankAccountId) => (this._unitBankAccountId = unitBankAccountId)
      );
  }

  ngOnDestroy(): void {
    this._destroyObserver$.next();
  }

  refreshUnitBusinessCards() {
    this._refreshUnitBusinessCards$.next();
  }

  updateUnitCards(unitCards: UnitCardDto[]) {
    this._unitCards$.next(unitCards);
  }

  addUnitCards(unitCard: UnitCardDto) {
    const unitCards = this._unitCards$.getValue();
    unitCards.push(unitCard);
    this.updateUnitCards(unitCards);
  }

  updateUnitBusinessCards(
    unitBusinessCards: UnitBusinessVirtualDebitCardDto[],
    setIndexTo: number = 0
  ) {
    this._unitBusinessCards$.next(unitBusinessCards);
    this.updateUnitBusinessCardIndex(setIndexTo);
  }

  updateUnitBusinessCardDraft(
    unitBusinessCardDraft: UnitBusinessVirtualDebitCardDto
  ) {
    this._unitBusinessCardDraft$.next(unitBusinessCardDraft);
  }

  resetUnitBusinessCardDraft(
    unitBusinessCardInUnit: UnitBusinessVirtualDebitCardDto
  ) {
    const draft = this._unitBusinessCardDraft$.getValue();
    if (draft.id === unitBusinessCardInUnit.id) {
      this._unitBusinessCardDraft$.next(null);
    }
  }

  updateUnitBusinessCardIndex(index: number) {
    this._unitBusinessCardInd$.next(index);
  }

  writeEventToTelemetry(eventTelemetryInstance: EventTelemetryInstance, depositType: string = null) {
    this.telemetryService.create(
      {
        eventID: eventTelemetryInstance.eventId,
        eventTitle: eventTelemetryInstance.eventTitle,
        unitBankAccountId: this._unitBankAccountId,
        depositType,
      },
      AnalyticsServiceNameModel.Mixpanel
    );
  }

  addUnitBusinessCards(unitBusinessCard: UnitBusinessVirtualDebitCardDto) {
    const unitBusinessCards = this._unitBusinessCards$.getValue();
    unitBusinessCards.push(unitBusinessCard);
    this.updateUnitBusinessCards(
      unitBusinessCards,
      unitBusinessCards.length - 1
    );
  }

  updateUnitBusinessCardsItem(
    unitBusinessCard: UnitBusinessVirtualDebitCardDto
  ) {
    const unitBusinessCards = this._unitBusinessCards$.getValue();
    const ind = unitBusinessCards.findIndex(
      (c) => c.id === unitBusinessCard.id
    );
    if (ind > -1) {
      unitBusinessCards.splice(ind, 1, {
        ...unitBusinessCard,
        isPinCodeExist: unitBusinessCards[ind].isPinCodeExist,
      });
      this._unitBusinessCardInd$.next(ind);
    }
  }

  allowViewSensitiveData() {
    this._viewSensitiveData$.next(true);
  }

  resetViewSensitiveData() {
    this._viewSensitiveData$.next(false);
    this.cdr.markForCheck();
  }

  updateLoadingSensitiveData(flag: boolean) {
    this._loadingSensitiveData$.next(flag);
  }

  updateUnitCustomerToken(
    model: UnitCustomerTokenDto,
    unitCustomerId: number,
    setInStorage: boolean
  ) {
    this._unitCustomerToken$.next(model);
    if (setInStorage) {
      this.storageService.setUnitCustomerToken(model, unitCustomerId);
    }
    this.cdr.markForCheck();
  }

  filterCardTransaction(filterC: string) {
    this.unitBankAccountService.filterCardTransactions$.next(filterC);
  }

  sensitiveData(unitCustomerId: number): Observable<UnitCustomerTokenDto> {
    return this.unitBusinessCard$.pipe(
      take(1),
      // tap(unitBusinessCard => console.log(unitBusinessCard)),
      switchMap((unitBusinessCard) =>
        this.verifyPhoneProcessCustomerToken(unitBusinessCard)
      ),
      tap((token) =>
        !!token ? this.updateUnitCustomerToken(token, unitCustomerId, true) : ''
      )
    );
  }

  private unitCustomerTokenIsValid(
    unitCustomerToken: UnitCustomerTokenDto
  ): boolean {
    if (!unitCustomerToken) {
      return false;
    }

    const expiresDate = moment(unitCustomerToken.expiresDate);
    const today = moment();

    if (!unitCustomerToken.token || expiresDate.isBefore(today)) {
      return false;
      // TerraUtils.compareDateWithNull
    }

    return true;
  }

  private verifyPhoneProcessCustomerToken(
    businessCard: UnitBusinessVirtualDebitCardDto
  ): Observable<UnitCustomerTokenDto> {
    const phoneNumberObject = businessCard.phone;
    const dialogConfig =
      new MatDialogConfig<CreateCustomerTokenDialogContext>();
    dialogConfig.data = new CreateCustomerTokenDialogContext();
    dialogConfig.disableClose = true;
    dialogConfig.data.phone = `${phoneNumberObject.countryCode}${phoneNumberObject.number}`;
    dialogConfig.viewContainerRef = this.vcr;
    return this.dialog
      .open(UnitCardCreateCustomerTokenComponent, dialogConfig)
      .afterClosed()
      .pipe(take(1));
  }

  getBusinessDebitCardByBankAccount(): Observable<
    UnitBusinessVirtualDebitCardDto[]
  > {
    return combineLatest(
      this._unitBankAccountId$,
      this._refreshUnitBusinessCards$
    ).pipe(
      switchMap(([unitBankAccountId, _refresher]) =>
        this.unitCardDataService.getBusinessDebitCardByBankAccount(
          unitBankAccountId
        )
      ),
      catchError((error) => {
        this.utilService.alertErrorMessage(error as BaseResponseDto);
        return throwError(() => error);
      }),
      switchMap((businessCards) =>
        businessCards && businessCards.length
          ? combineLatest([
            ...businessCards
              .filter(
                (card) => card.cardStatus !== UnitCardStatus.ClosedByCustomer
              )
              .map((card) => this.getDebitCardPinCodeStatus(card)),
            ...businessCards
              .filter(
                (card) => card.cardStatus === UnitCardStatus.ClosedByCustomer
              )
              .map((card) => of(card)),
          ])
          : of([])
      ),
      tap((businessCards) => this.updateUnitBusinessCards(businessCards)),
      shareReplay(1)
    );
  }

  getBusinessDebitCardDraft(): Observable<UnitBusinessVirtualDebitCardDto> {
    return this._unitBankAccountId$.pipe(
      take(1),
      switchMap((unitBankAccountId) =>
        this.unitCardDataService.getBusinessDebitCardDraft(unitBankAccountId)
      ),
      catchError((error) => {
        this.utilService.alertErrorMessage(error as BaseResponseDto);
        return throwError(() => error);
      }),
      tap((businessCard) => this.updateUnitBusinessCardDraft(businessCard))
    );
  }

  saveBusinessDebitCardDraft(
    model: UnitBusinessVirtualDebitCardDto
  ): Observable<UnitBusinessVirtualDebitCardDto> {
    return this._unitBankAccountId$.pipe(
      take(1),
      tap((unitBankAccountId) => (model.unitBankAccountId = unitBankAccountId)),
      switchMap((unitBankAccountId) =>
        this.unitCardDataService.saveBusinessDebitCardDraft(
          unitBankAccountId,
          model
        )
      ),
      catchError((error) => {
        this.utilService.alertErrorMessage(error as BaseResponseDto);
        return throwError(() => error);
      }),
      tap((bcd) => this.updateUnitBusinessCardDraft(bcd))
    );
  }

  updateBusinessDebitCardDraft(
    businessCardId: number,
    model: UnitBusinessVirtualDebitCardDto
  ): Observable<UnitBusinessVirtualDebitCardDto> {
    return this._unitBankAccountId$.pipe(
      take(1),
      switchMap((unitBankAccountId) =>
        this.unitCardDataService.updateBusinessDebitCard(
          unitBankAccountId,
          businessCardId,
          model
        )
      ),
      catchError((error) => {
        this.utilService.alertErrorMessage(error as BaseResponseDto);
        return throwError(() => error);
      }),
      tap((bcd) => this.updateUnitBusinessCardDraft(bcd))
    );
  }

  createBusinessDebitCardOnUnit(
    businessCardId: number
  ): Observable<UnitBusinessVirtualDebitCardDto> {
    return this._unitBankAccountId$.pipe(
      take(1),
      switchMap((unitBankAccountId) =>
        this.unitCardDataService.createBusinessDebitCardOnUnit(
          unitBankAccountId,
          businessCardId
        )
      ),
      catchError((error) => {
        this.utilService.alertErrorMessage(error as BaseResponseDto);
        return throwError(() => error);
      }),
      tap((businessCard) => {
        this.resetUnitBusinessCardDraft(businessCard);
      })
    );
  }

  updateBusinessDebitCard(
    businessCardId: number,
    model: UnitBusinessVirtualDebitCardDto
  ): Observable<UnitBusinessVirtualDebitCardDto> {
    return this._unitBankAccountId$.pipe(
      take(1),
      switchMap((unitBankAccountId) =>
        this.unitCardDataService.updateBusinessDebitCard(
          unitBankAccountId,
          businessCardId,
          model
        )
      ),
      catchError((error) => {
        this.utilService.alertErrorMessage(error as BaseResponseDto);
        return throwError(() => error);
      }),
      tap((bc) => this.updateUnitBusinessCardsItem(bc))
    );
  }

  sendVerificationCode(): Observable<PhoneVerificationReqRes> {
    return this.unitBusinessCardDraft$.pipe(
      take(1),
      switchMap((unitBusinessCard) =>
        this.unitCardDataService.sendVerificationCode(unitBusinessCard.id)
      ),
      tap((_) =>
        this.writeEventToTelemetry(
          this._isLpSide
            ? EventsTelemetry.LP_DebitCardCreateOTPSent
            : EventsTelemetry.GP_DebitCardCreateOTPSent
        )
      ),
      catchError((error) => {
        this.writeEventToTelemetry(
          this._isLpSide
            ? EventsTelemetry.LP_DebitCardCreateOTPCodeWrong
            : EventsTelemetry.GP_DebitCardCreateOTPCodeWrong
        );
        this.utilService.alertErrorMessage(error as BaseResponseDto);
        return throwError(() => error);
      })
    );
  }

  resendVerificationCode(): Observable<PhoneVerificationReqRes> {
    return this.unitBusinessCardDraft$.pipe(
      take(1),
      switchMap((unitBusinessCard) =>
        this.unitCardDataService.resendVerificationCode(unitBusinessCard.id)
      ),
      catchError((error) => {
        this.utilService.alertErrorMessage(error as BaseResponseDto);
        return throwError(() => error);
      })
    );
  }

  verifyPhone(
    request: PhoneVerificationReq
  ): Observable<PhoneVerificationReqRes> {
    return this.unitBusinessCardDraft$.pipe(
      take(1),
      switchMap((unitBusinessCard) =>
        this.unitCardDataService.verifyPhone(unitBusinessCard.id, request)
      ),
      catchError((error) => {
        this.utilService.alertErrorMessage(error as BaseResponseDto);
        return throwError(() => error);
      })
    );
  }

  createBusinessVirtualDebitCardVerificationToken(): Observable<UnitCustomerTokenDto> {
    return this.unitBusinessCardId$.pipe(
      take(1),
      switchMap((unitBusinessCardId) =>
        this.unitCardDataService.createBusinessVirtualDebitCardVerificationToken(
          unitBusinessCardId
        )
      ),
      catchError((error) => {
        this.utilService.alertErrorMessage(error as BaseResponseDto);
        return throwError(() => error);
      })
    );
  }

  createCardCustomerTokenAsync(
    model: UnitCustomerTokenDto
  ): Observable<UnitCustomerTokenDto> {
    return this.unitBusinessCardId$.pipe(
      take(1),
      switchMap((unitBusinessCardId) =>
        this.unitCardDataService.createCardCustomerTokenAsync(
          unitBusinessCardId,
          model
        )
      ),
      catchError((error) => {
        this.utilService.alertErrorMessage(error as BaseResponseDto);
        return throwError(() => error);
      })
    );
  }

  freezeBusinessDebitCard(): Observable<UnitBusinessVirtualDebitCardDto> {
    return this.unitBusinessCardId$.pipe(
      take(1),
      switchMap((unitBusinessCardId) =>
        this.unitCardDataService.freezeBusinessDebitCard(unitBusinessCardId)
      ),
      catchError((error) => {
        this.utilService.alertErrorMessage(error as BaseResponseDto);
        return throwError(() => error);
      }),
      tap((unitBusinessCard) =>
        this.updateUnitBusinessCardsItem(unitBusinessCard)
      )
    );
  }

  unFreezeBusinessDebitCard(): Observable<UnitBusinessVirtualDebitCardDto> {
    return this.unitBusinessCardId$.pipe(
      take(1),
      switchMap((unitBusinessCardId) =>
        this.unitCardDataService.unFreezeBusinessDebitCard(unitBusinessCardId)
      ),
      catchError((error) => {
        this.utilService.alertErrorMessage(error as BaseResponseDto);
        return throwError(() => error);
      }),
      tap((unitBusinessCard) =>
        this.updateUnitBusinessCardsItem(unitBusinessCard)
      )
    );
  }

  getDebitCardPinCodeStatus(
    card: UnitBusinessVirtualDebitCardDto
  ): Observable<UnitBusinessVirtualDebitCardDto> {
    return this.unitCardDataService
      .getBusinessDebitCardPinCodeStatus(card.id)
      .pipe(
        map((status) => ({
          ...card,
          isPinCodeExist: status === UnitPinStatus.Set,
        })),
        catchError((error) => {
          this.utilService.alertErrorMessage(error as BaseResponseDto);
          return throwError(() => error);
        })
      );
  }

  doDebitCardPinCodeAction(
    action: UnitCardPinAction,
    successMessage: string,
    failureMessage: string
  ): Observable<void> {
    return combineLatest([
      this.unitBusinessCard$,
      this.unitCustomerToken$,
    ]).pipe(
      take(1),
      filter(
        ([unitBusinessCard, _]) =>
          unitBusinessCard.cardStatus !== UnitCardStatus.ClosedByCustomer
      ),
      switchMap(([unitBusinessCard, customerToken]) => {
        const params = new UnitCardPinParams(
          action,
          unitBusinessCard,
          customerToken.token
        );
        return this.dialogService
          .customDialog<UnitCardPinDialogComponent>(
            UnitCardPinDialogComponent,
            params
          )
          .afterClosed();
      }),
      tap((result) =>
        result?.isOk
          ? this.handlePinCodeActionOkResult(successMessage, result.card)
          : !result
            ? null
            : throwError(() => new Error())
      ),
      catchError((_) => {
        this.snackbarService.showGeneralMessage(failureMessage, 5);
        return EMPTY;
      })
    );
  }

  handlePinCodeActionOkResult(
    successMessage: string,
    card: UnitBusinessVirtualDebitCardDto
  ) {
    this.updateUnitBusinessCardsItem(card);
    this.snackbarService.showGeneralMessage(successMessage, 5);
  }

  cancelBusinessDebitCard(): Observable<UnitBusinessVirtualDebitCardDto> {
    return this.unitBusinessCardId$.pipe(
      take(1),
      switchMap((unitBusinessCardId) =>
        this.unitCardDataService.cancelBusinessDebitCard(unitBusinessCardId)
      ),
      catchError((error) => {
        this.utilService.alertErrorMessage(error as BaseResponseDto);
        return throwError(() => error);
      }),
      tap((unitBusinessCard) =>
        this.updateUnitBusinessCardsItem(unitBusinessCard)
      )
    );
  }
}
